/**
 * Formatting utilities for video annotation and documentation.
 * Uses shared formatters to ensure consistency between documentation and actual output.
 */

import { TextAlignment, VideoData } from '@lumen5/luminary';

import { ASSET_TYPES } from '../constants';
import type {
    CaptionDescription,
    ColorAssetDescription,
    DocumentationExample,
    GroupAssetDescription,
    IconAssetDescription,
    MediaAssetDescription,
    StickerAssetDescription,
    TextAssetDescription,
} from '../types';

import { formatAsset } from './assetFormatters';

const colorAssetFormat = (): string => {
    const example: ColorAssetDescription = {
        type: ASSET_TYPES.BACKGROUND,
        name: '<asset_name>',
        bounds: '<x, y, width, height>',
        color: '<color as 24-bit integer>',
    };
    return '\n' + formatAsset(example);
};

const CaptionFormat = (): string => {
    const example: CaptionDescription = {
        type: ASSET_TYPES.CAPTION,
        name: '<asset_name>',
        bounds: '<x, y, max_width, max_height>',
    };
    return '\n' + formatAsset(example);
};

const textAssetFormat = (): string => {
    const example: DocumentationExample<TextAssetDescription> = {
        type: ASSET_TYPES.TEXT,
        name: '<asset_name>',
        bounds: '<x, y, max_width, max_height>',
        text: '<text content>',
        textAlignment: `<${TextAlignment.LEFT} | ${TextAlignment.CENTER} | ${TextAlignment.RIGHT}>`,
        textColors: '<normal and highlight text color as 24-bit integer>',
        textLines: '<number of text lines>',
        fontFamily: '<font family>',
        fontSize: '<font size>',
    };
    return '\n' + formatAsset(example as TextAssetDescription);
};

const iconAssetFormat = (): string => {
    const example: IconAssetDescription = {
        type: ASSET_TYPES.ICON,
        name: '<asset_name>',
        bounds: '<x, y, width, height>',
        iconColors: '<comma-separated list of colors as 24-bit integers>',
    };
    return '\n' + formatAsset(example);
};

const groupAssetFormat = (): string => {
    const example: GroupAssetDescription = {
        type: ASSET_TYPES.GROUP,
        name: '<asset_name>',
        bounds: '<x, y, width, height>',
        children: '<comma-separated list of child asset names>',
        childrenAlignment: `<${VideoData.LEFT_ALIGNMENT} | ${VideoData.CENTER_ALIGNMENT} | ${VideoData.RIGHT_ALIGNMENT}>`,
    };
    return '\n' + formatAsset(example);
};

const mediaAssetFormat = (): string => {
    const example: MediaAssetDescription = {
        type: ASSET_TYPES.MEDIA,
        name: '<asset_name>',
        bounds: '<x, y, width, height>',
        mediaId: '<media ID>',
        mediaDescription: '<description>',
        mediaType: '<image/video>',
        mediaScale: '<scale%>',
        mediaResolution: '<width, height>',
        mediaTranslation: '<x, y>',
        mediaXTranslationRange: '<min, max>',
        mediaYTranslationRange: '<min, max>',
    };
    return '\n' + formatAsset(example);
};

const stickerAssetFormat = (): string => {
    const example: StickerAssetDescription = {
        type: ASSET_TYPES.STICKER,
        name: '<asset_name>',
        bounds: '<x, y, width, height>',
        mediaId: '<media ID>',
    };
    return '\n' + formatAsset(example);
};

export const getAssetAnnotationFormat = (): string => {
    return (
        groupAssetFormat() +
        textAssetFormat() +
        iconAssetFormat() +
        mediaAssetFormat() +
        stickerAssetFormat() +
        colorAssetFormat() +
        CaptionFormat()
    );
};

export const getHowOurVideosWorkGuide = (): string => {
    return `## How our videos work

There are certain specifics to how our videos work that you should be aware of:
- Assets on a scene cannot be added or removed, only modified.
- Text assets with no text set are not displayed in the video.
- Videos must have at least one scene.
- Scene groups can be one of several types: "regular" (no audio — the video visually needs to communicate the whole message), "ai_voiceover" (AI-generated voiceover audio), "recorded_voiceover" (a human-recorded voiceover), "ai_talking_head" (an AI avatar speaking), or "recorded_talking_head" (a real person speaking on camera). Check the scene group type to understand how each part of the video communicates its message.
- Scene indexes are 0-based but users are shown 1-based indexes in the UI. When referring to scenes, always use 0-based indexing internally and 1-based indexing when communicating with users.
- "highlighted" text is displayed with "**" around it, indicating that it is rendered in a special way. Remove the "**" when you want to edit or display the text, as it is not part of the actual content.`;
};

export const getVideoAnnotationGuide = (): string => {
    return `## Video Annotation:
This provides you with the complete video context.

The video annotation format describes the video structure:
${getVideoAnnotationFormat()}

Each asset follows specific annotation syntax:
${getAssetAnnotationFormat()}
`;
};

export const getVideoAnnotationFormat = (): string => {
    return `
- SCENE GROUP <index> groups one or more scenes that share timing (e.g., voiceover, talking head)
  - Types: regular (no audio), ai_voiceover (AI voiceover), recorded_voiceover (human voiceover), ai_talking_head (AI avatar speaker), recorded_talking_head (real person on camera)
  - Contains: sceneIndices (which scenes belong to this group), duration, and transcript (word timings for audio-driven groups)
  - Transcript includes word-by-word timing (startTime/endTime in ms) for overlay placement decisions
- SCENE <index> contains assets that define the layout and content
  - Each scene belongs to exactly one scene group (referenced by sceneGroupIndex)
- Asset types include:
  - ${ASSET_TYPES.TEXT}: text rendered on the scene with size, max_chars, and editability
  - ${ASSET_TYPES.CAPTION}: text rendered on the scene; content is controlled by transcript (read-only), but size can be adjusted
  - ${ASSET_TYPES.FANCY_CAPTION}: text rendered on the scene; content is controlled by transcript (read-only), but position/size can be adjusted
  - ${ASSET_TYPES.MEDIA}: image or video displayed on the scene with dimensions and editability
  - ${ASSET_TYPES.ICON}: icon displayed on the scene with dimensions and editability
  - ${ASSET_TYPES.STICKER}: sticker displayed on the scene with dimensions and editability
  - ${ASSET_TYPES.GROUP}: alignment container for child assets (not visible)
  - ${ASSET_TYPES.WATERMARK}: watermark image displayed on the scene
  - ${ASSET_TYPES.BACKGROUND}: solid background color with children`;
};

export const getDiffFormatGuide = (): string => {
    return `## Understanding Video State Diffs

Tools that modify the video automatically return a git-style diff showing what changed. This helps you track the evolution of the video over time.

### Diff Format Symbols
- \`+\` prefix: Property or asset was **added**
- \`-\` prefix: Property or asset was **removed**
- No prefix (context lines): Shows which asset is being modified
- \`@@ Section Name @@\`: Header indicating which part of video changed

### When You See Diffs
- **Initial state**: All properties shown with \`+\` (establishing baseline)
- **After tool execution**: Only changed properties shown with \`-\` (old) and \`+\` (new)
- **Modified assets**: Use shorthand \`asset: name\` to identify which asset changed

### Initial Video State Example
When you first start, the entire video state is shown as a "baseline":
\`\`\`diff
@@ Initial Video State @@
+ resolution: 1920x1080
+ aspectRatio: landscape
+ sceneCount: 1
+ duration: 3400ms
@@ Template @@
+ template: minimal
+ templateFonts: NotoSansLight-814bb, NotoSansRegular-68ecf, NotoSansBold-5ff19
+ templateColors: 16777215(white), 4836281(turquoise), 1975411(indigo), 1405027(teal)
@@ Music @@
+ music: No music set
@@ Scene Groups @@
+ - index: 0
+   type: regular
+   scenes: [0]
+   duration: 3400ms
@@ Scene index 0 @@
+ sceneGroupIndex: 0
+ sceneDesign: minimal:text_1
+ duration: 3400ms
+ subscenes: 1
+ textPositionPresets: [bottom_right]
+ - type: BACKGROUND
+   name: color_1
+   bounds: "0 0 1920 1080"
+   color: "16777215(white)"
+ - type: TEXT
+   name: text_1
+   bounds: "651 509 617 62"
+   text: "Love is in the air."
+   textAlignment: "left"
+   textColors: "0(black)"
+   textLines: 1
+   fontSize: 80
+   fontFamily: "NotoSansRegular-68ecf"
\`\`\`
Everything has \`+\` because this is the starting point. Future diffs will show changes relative to this baseline.

### Scene Group with Voiceover Example
When a scene group has a voiceover, the transcript is included:
\`\`\`diff
@@ Scene Groups @@
+ - index: 0
+   type: ai_voiceover
+   scenes: [0, 1, 2]
+   duration: 15000ms
+   transcript:
+     language: en
+     words:
+       Hello
+       and
+       welcome
+       to
+       our
+       presentation
\`\`\`

### Example Progression

#### Example 1: Adding a New Asset
After calling \`add_sticker\` tool, a new sticker asset appears:
\`\`\`diff
@@ Scene index 0 (Modified) @@
+ - type: STICKER
+   name: sticker_1
+   bounds: "0 0 1 1"
+   mediaId: "<unknown>"
\`\`\`
The \`+\` on every line means this entire asset is new.

#### Example 2: Setting Sticker Media
After calling \`set_sticker\` tool to assign a specific sticker image, the tool automatically sets the mediaId and recalculates bounds based on the actual sticker dimensions:
\`\`\`diff
@@ Scene index 0 (Modified) @@
  asset: sticker_1
-   bounds: "754 291 413 498"
+   bounds: "753 11 413 498"
-   mediaId: "<unknown>"
+   mediaId: "T2PmQ9"
\`\`\`
Both \`bounds\` and \`mediaId\` changed together because the tool loads the media and adjusts dimensions to fit the actual sticker.

#### Example 3: Seeing Full Asset State
Sometimes tools show the complete current state of an asset:
\`\`\`diff
@@ Scene index 0 (Modified) @@
+ - type: STICKER
+   name: sticker_1
+   bounds: "753 11 413 498"
+   mediaId: "T2PmQ9"
\`\`\`
The \`+\` prefix means "this is what exists now" (useful for context).

**Important**: Every tool that modifies the video will return a diff in this format. Use these diffs to track what changed and build your understanding of the current video state.`;
};
